import { useCallback, useEffect, useMemo, useState } from 'react';
import { FlatList, SafeAreaView, Text, View } from 'react-native';
import { CommonActions, useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { doc, setDoc } from 'firebase/firestore';
import Header from '../../components/Header';
import CategoryList from '../../components/CategoryList';
import { FilterBar } from '../../components/FilterBar';
import AdminFiltersModal from '../AdminPanel/Filters';
import Button from '../../components/Button';
import TitlePromptModal from '../../components/TitlePromptModal';
import { ViewButton } from '../Main/style';
import { useCategories, refreshCategories } from '../../queries/useCategories';
import { useFilters } from '../../queries/useFilters';
import { useFiltersSettings } from '../../queries/useFiltersSettings';
import { useProductCatalogFilters } from '../../queries/useProductCatalogFilters';
import { useCatalogProducts } from '../../queries/useCatalogProducts';
import { useRemoteConfigContext } from '../../contexts/useRemoteConfigContext';
import { getCatalogGroup } from '../../utils/accessUtils';
import {
  applyProductCatalogFilter,
  applyProductScopedCatalogFilter,
  buildProductCategories,
  CATALOG_COLLECTION_BY_TYPE,
  getEffectiveProductCatalogFilters,
  getEffectiveProductScopedCatalogFilters,
  getProductCatalogCardKeys,
  mapProductScopedFiltersForBar,
} from '../../utils/productCatalog';
import { useCustomTheme } from '../../contexts/useCustomTheme';
import { useAuthUser } from '../../contexts/useAuthUser';
import { firestore } from '../../services/firebase';
import THEME from '../../config/theme';
import ViewPortProvider from '../../hooks/ViewPortProvider';
import { firestoreAutoId, removeSansSerif } from '../../utils/validators';

// Mesmo recuo lateral do mobile (RFValue(16)): título, filtros e módulos alinhados.
const CONTENT_PADDING = 16;

export function ProductCatalog() {
  const { params } = useRoute();
  const { title, productId } = params;
  const navigation = useNavigation();
  const { user } = useAuthUser();
  const { customColor, customFont } = useCustomTheme();
  const { data: allCategories = [], refetch: refetchCategories } = useCategories();
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);

  const hasAdminRole = !user?.adminRole;

  // Web é SPA: trocar de rota não reseta o scroll do navegador como um
  // carregamento de página normal faria — sem isto a tela abria na mesma
  // posição em que o aluno estava na Main.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [productId]);

  // Admin volta de editar card/categoria ou altera publicação: recarrega a lista.
  useFocusEffect(
    useCallback(() => {
      if (user?.isAdmin) {
        refetchCategories();
      }
    }, [user?.isAdmin, refetchCategories])
  );

  // O grupo de ids não vem pela URL (ver ProductItem): é recalculado aqui a
  // partir do próprio produto, buscado de novo por `productId`. Isso também
  // sobrevive a um refresh da página.
  const { hasProductCatalog } = useRemoteConfigContext();
  const { data: catalogProducts = [] } = useCatalogProducts();
  const product = catalogProducts.find((p) => p.id === productId);
  const group = useMemo(() => (product ? getCatalogGroup(product) : []), [product]);
  const productCollectionName =
    CATALOG_COLLECTION_BY_TYPE[product?.type] || CATALOG_COLLECTION_BY_TYPE.product;

  const { data: productScopedFilters = [] } = useProductCatalogFilters(
    productCollectionName,
    productId,
    hasProductCatalog
  );

  const publishedProductFilters = useMemo(
    () => productScopedFilters.filter((filter) => !filter.archived),
    [productScopedFilters]
  );

  const usesProductScopedFilters =
    hasProductCatalog && publishedProductFilters.length > 0;

  const { data: filters = [], isLoading: isFiltersLoading } = useFilters();
  const { data: filterSettings, isLoading: isSettingsLoading } = useFiltersSettings();
  // Product filters (Products tab) are independent from the global Smart
  // Filters: they must show even when the global filters are not configured.
  const hasFiltersEnabled =
    usesProductScopedFilters || (filterSettings != null && !isSettingsLoading);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [modalFilterVisible, setModalFilterVisible] = useState(false);

  const productCategories = useMemo(
    () => buildProductCategories(allCategories, group, user),
    [allCategories, group, user]
  );

  const selectedScopedFilter = useMemo(
    () => publishedProductFilters.find((filter) => filter.slug === selectedFilter) ?? null,
    [publishedProductFilters, selectedFilter]
  );

  const categoriesWithCards = useMemo(() => {
    const sorted = (categories) =>
      categories.map((category) => ({
        ...category,
        cards: [...category.cards].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
      }));

    if (!hasFiltersEnabled || !selectedFilter) {
      return sorted(productCategories);
    }

    if (usesProductScopedFilters) {
      return sorted(applyProductScopedCatalogFilter(productCategories, selectedScopedFilter));
    }

    return sorted(applyProductCatalogFilter(productCategories, selectedFilter));
  }, [
    productCategories,
    hasFiltersEnabled,
    selectedFilter,
    usesProductScopedFilters,
    selectedScopedFilter,
  ]);

  const validFilters = useMemo(() => {
    if (usesProductScopedFilters) {
      return mapProductScopedFiltersForBar(
        getEffectiveProductScopedCatalogFilters(publishedProductFilters, productCategories)
      );
    }

    return getEffectiveProductCatalogFilters(filters, productCategories);
  }, [
    usesProductScopedFilters,
    publishedProductFilters,
    productCategories,
    filters,
  ]);

  const searchRouteParams = useMemo(
    () => ({
      productCatalogOrigin: { title, productId },
      allowedCardKeys: getProductCatalogCardKeys(productCategories),
    }),
    [title, productId, productCategories]
  );

  const handleSelectFilter = (value) => {
    if (!hasFiltersEnabled) return;
    setSelectedFilter(value);
  };

  const newCategoryTemplate = useMemo(
    () => ({
      title: 'NOME CATEGORIA',
      order: allCategories.length + 1,
      archived: true,
      cards: [
        {
          title: 'Nome do Módulo',
          description: '',
          order: 1,
          archived: true,
        },
      ],
    }),
    [allCategories.length]
  );

  const firestoreAddCategory = async (categoryTitle, hideTitle) => {
    const firestoreId = firestoreAutoId();
    const categoriesCollectionRef = doc(firestore, 'categories', firestoreId);
    await setDoc(
      categoriesCollectionRef,
      {
        title: categoryTitle,
        hideTitle: !!hideTitle,
        order: newCategoryTemplate.order,
        archived: newCategoryTemplate.archived,
      },
      { merge: true }
    );
    const cardsRef = doc(
      firestore,
      'categories',
      firestoreId,
      'cards',
      firestoreAutoId()
    );
    await setDoc(
      cardsRef,
      {
        title: categoryTitle,
        hideTitle: !!hideTitle,
        description: newCategoryTemplate.cards[0].description,
        order: newCategoryTemplate.cards[0].order,
        archived: newCategoryTemplate.cards[0].archived,
      },
      { merge: true }
    );
    await refreshCategories();
    await refetchCategories();
  };

  const titleFontSize =
    customFont && customFont.fontLG ? customFont.fontLG : THEME.FONTSIZE.BIG;
  const titleLineHeight = titleFontSize * 1.35;
  const titleFontFamily =
    customFont && customFont.fontFamilyTitle
      ? removeSansSerif(customFont.fontFamilyTitle)
      : THEME.FONTFAMILY.BOLD;

  const handleGoBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
      return;
    }

    navigation.dispatch(
      CommonActions.navigate(
        user?.isAdmin
          ? { name: 'Admin', params: { screen: 'Conteudo' } }
          : { name: 'Conteudo', params: { screen: 'Aplicativo' } }
      )
    );
  };

  return (
    <ViewPortProvider>
      <SafeAreaView
        style={{
          flexGrow: 1,
          backgroundColor:
            customColor && customColor.background && customColor.background.app
              ? customColor.background.app
              : THEME.COLORS.BACKGROUND_MAIN,
        }}
      >
        <Header
          onPress={handleGoBack}
          user={user}
          isPageMain
          hideCommunity
          searchRouteParams={searchRouteParams}
          onOpenFilters={() => setModalFilterVisible(true)}
        />
        <AdminFiltersModal
          visible={modalFilterVisible}
          onClose={() => setModalFilterVisible(false)}
          onRefreshCategories={refreshCategories}
        />
        <Text
          numberOfLines={2}
          ellipsizeMode="tail"
          style={{
            color:
              customColor && customColor.text && customColor.text.app
                ? customColor.text.app
                : THEME.COLORS.TITLE_MAIN,
            fontSize: titleFontSize,
            lineHeight: titleLineHeight,
            fontFamily: titleFontFamily,
            paddingHorizontal: CONTENT_PADDING,
            marginTop: 8,
            marginBottom: 8,
            minHeight: titleLineHeight * 2,
            textAlign: 'left',
          }}
        >
          {title}
        </Text>

        {hasFiltersEnabled && !isFiltersLoading && validFilters.length > 1 && (
          <FilterBar
            filters={validFilters}
            selected={selectedFilter}
            onSelect={handleSelectFilter}
            contentPaddingHorizontal={CONTENT_PADDING}
            colors={
              customColor?.iconsMain?.catalogFilters ||
              customColor?.iconsMain?.icons ||
              THEME.COLORS.ICON_MAIN
            }
            fonts={
              customFont && customFont.fontFamilyText
                ? removeSansSerif(customFont.fontFamilyText)
                : THEME.FONTFAMILY.REGULAR
            }
            showClear={selectedFilter !== null}
          />
        )}

        <FlatList
          data={categoriesWithCards}
          keyExtractor={(item) => item.id}
          extraData={categoriesWithCards}
          contentContainerStyle={{ paddingTop: 20, paddingBottom: user?.isAdmin ? 80 : 20 }}
          renderItem={({ item }) => (
            <CategoryList
              category={item}
              user={user}
              navigation={navigation}
              selectedFilter={selectedFilter}
              productCatalogOrigin={{ title, productId }}
              horizontalPadding={CONTENT_PADDING}
              onRefreshCategories={async () => {
                await refreshCategories();
                await refetchCategories();
              }}
            />
          )}
        />

        {user?.isAdmin && hasAdminRole && (
          <View
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 16,
              zIndex: 20,
              alignItems: 'center',
            }}
          >
            <ViewButton>
              <Button
                title="+ NOVA CATEGORIA"
                onPress={() => setShowNewCategoryModal(true)}
                fontFamily={
                  customFont && customFont.fontFamilyText
                    ? removeSansSerif(customFont.fontFamilyText)
                    : THEME.FONTFAMILY.REGULAR
                }
                fontSize={14}
                borderRadius={10}
              />
            </ViewButton>
          </View>
        )}

        <TitlePromptModal
          visible={showNewCategoryModal}
          entityLabel="categoria"
          onCancel={() => setShowNewCategoryModal(false)}
          onConfirm={async (categoryTitle, hideTitle) => {
            setShowNewCategoryModal(false);
            await firestoreAddCategory(categoryTitle, hideTitle);
          }}
        />
      </SafeAreaView>
    </ViewPortProvider>
  );
}